import { useEffect, useRef, useState } from 'react';

interface Parsed {
  prefix: string;
  suffix: string;
  target: number;
  /** Anzahl der Nachkommastellen im Ausgangswert. */
  fractionDigits: number;
}

/**
 * Zerlegt einen formatierten Wert in Vorspann, Zahl und Nachspann.
 *
 * Wichtig: die Oberflaeche formatiert durchgaengig deutsch, also Punkt als
 * Tausender- und Komma als Dezimaltrennzeichen ("1.284" und "1,9"). Wird das
 * verwechselt, zeigt die Kennzahl voellig falsche Werte an - deshalb wird hier
 * ausdruecklich nach deutscher Schreibweise geparst.
 */
function parse(value: string | number): Parsed | null {
  if (typeof value === 'number') {
    return Number.isFinite(value)
      ? { prefix: '', suffix: '', target: value, fractionDigits: 0 }
      : null;
  }

  const match = value.match(/^(\D*?)(\d[\d.]*(?:,\d+)?)(.*)$/);
  if (!match) return null;

  const prefix = match[1] ?? '';
  const num = match[2] ?? '';
  const suffix = match[3] ?? '';

  const [integerPart = '', fractionPart = ''] = num.split(',');
  const target = Number.parseFloat(`${integerPart.replace(/\./g, '')}.${fractionPart || '0'}`);
  if (Number.isNaN(target)) return null;

  return { prefix, suffix, target, fractionDigits: fractionPart.length };
}

const prefersReduced =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** In versteckten Tabs laufen keine Animationsbilder. */
function isHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * Zaehlt beim Einblenden von 0 auf den Zielwert hoch und behaelt dabei
 * Vorspann, Einheit und Nachkommastellen bei. Nicht numerische Werte
 * (z.B. "1 / 32") werden unveraendert ausgegeben.
 */
export function AnimatedNumber({
  value,
  duration = 1100,
  className,
}: {
  value: string | number;
  duration?: number;
  className?: string;
}) {
  const parsed = parse(value);
  const animatable = Boolean(parsed) && !prefersReduced && !isHidden();
  const [display, setDisplay] = useState(() =>
    parsed && animatable ? format(parsed, 0) : String(value),
  );
  const frame = useRef<number>(0);

  useEffect(() => {
    if (!parsed || !animatable) {
      setDisplay(String(value));
      return;
    }

    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      // Am Ende exakt den Ausgangswert zeigen, nicht den gerundeten Zwischenstand
      setDisplay(progress < 1 ? format(parsed, parsed.target * eased) : String(value));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);

    /*
     * Sicherheitsnetz: in versteckten Tabs und unsichtbaren Fenstern liefert der
     * Browser keine Animationsbilder. Ohne diesen Zeitgeber bliebe die Anzeige
     * dann auf dem Startwert stehen und zeigte eine falsche Zahl.
     */
    const settle = window.setTimeout(() => setDisplay(String(value)), duration + 150);

    return () => {
      cancelAnimationFrame(frame.current);
      window.clearTimeout(settle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return <span className={className}>{display}</span>;
}

function format(parsed: Parsed, current: number): string {
  const num = current.toLocaleString('de-DE', {
    minimumFractionDigits: parsed.fractionDigits,
    maximumFractionDigits: parsed.fractionDigits,
  });
  return `${parsed.prefix}${num}${parsed.suffix}`;
}
